import React, { useState } from 'react';

import TextInput from '../Form/TextInput/TextInput';
import Button from '../Form/Button/Button';
import {LoadingButton} from '../Form/Button/Button';
import { useMutation } from '@apollo/react-hooks';
import { gql } from 'apollo-boost';
import ResendOtp from '../ResendOtp/ResendOtp';
import ResendActivationOtp from '../ResendOtp/ResendActivationOtp';
import withFeatureFlags from '../FeatureFlags/withFeatureFlags';

/** @import {WithFeatureFlagsProps} from '../FeatureFlags/FeatureFlagsTypes' */
/** @import {VerifyOtpProps, VerifyOtpResult} from './VerifyOtpTypes' */

import './login.scss';

function VerifyOtp(/** @type {VerifyOtpProps & WithFeatureFlagsProps} */ props) {
  const VERIFY_OTP = gql`
  mutation VerifyOtp($request : VerifyOtpRequestType!){
    verifyOtp(request:$request) {
      success
      email
      multipleEmails
      completionToken
    }
  }  
`;

  const [otp, setOtp] = useState('');
  const [verificationError, setVerificationError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [unexpectedError, setUnexpectedError] = useState(false);
  const [otpMode, setOtpMode] = useState('sms');

  const [verifyInputOtp] = useMutation(VERIFY_OTP);
  
  const isValid = () => {
    return (otp.length === 5 || otp.length === 6) && !isResending;
  };

  const setOtpValue = (/** @type {string} */ v) => {
    if (/[0-9]+/.test(v) || v === '') {
      setVerificationError(false);
      setOtp(v);
    }
  };

  const renderNextButton = (/** @type {React.ReactNode} */ label, /** @type {boolean} */ isLoading, /** @type {boolean | undefined} */ disabled, /** @type {(() => void) | undefined} */ callback) => {
    return isLoading ?
      <LoadingButton customClassNames={['loginButton', 'button--primary']} /> :
      <Button customClassNames={['loginButton', 'button--primary']} disabled={disabled} label={label} onClick={callback} />
  };

  const nextClicked = async (/** @type {React.MouseEvent<HTMLLabelElement, MouseEvent>} */ e) => {
    e.preventDefault();

    setIsLoading(true);

    try {
      const result = props.customVerify ? 
        await props.customVerify(otp, otpMode) :
        await builtInVerification();
      if (result.success) {
        if (props.onSuccess) {
          props.onSuccess(result.data.email, result.data.multipleEmails, result.data.completionToken);
        }
      }
      else if (result.message) {
        if (result.message.includes("UnexpectedError")) {
        setUnexpectedError(true);
        }
        else if (result.message.includes("VerifyingOtpError")) {
          setVerificationError(true);
        }
      }
      else {
        setVerificationError(true);
        }
      setIsLoading(false);

    }
    catch (e) {
      setVerificationError(true);
      setIsLoading(false);
    }
  }
  
  /** @returns {Promise<VerifyOtpResult>} */
  const builtInVerification = async () => {
    const result = await verifyInputOtp({
      variables: {
        request: {
          passCode: otp,
          verificationToken: props.verificationToken,
          mode: otpMode
        }
      }
    });

    return {
      success: result.data.verifyOtp.success,
      data: result.data.verifyOtp
    };
  };

  const getSentText = () => {
    const textPass = 'passcode';
    const usefullstop = '.';
    if (props.featureEnabled('voice_otp'))
      return otpMode === 'sms'
        ? <>Please enter the one-time {textPass} we texted you{usefullstop}</>
        : <>Please enter the one-time {textPass} we called you{usefullstop}</>;
    

    return props.noNumberMessage
      ? <>We’ve sent you a one-time {textPass} via text, please enter it here:</>
      : <>We’ve sent a one-time {textPass} via text to <strong>{props.mobileNumber}</strong>, please enter it here:</>;
  }

  return (
    <form className='otpForm'>
      <div className="login__line">
        {getSentText()}
      </div>
      <label>One-time passcode</label>
      <TextInput
        className="login__input"
        value={otp}
        onChange={v => setOtpValue(v)}
        type="text"
        maxLength={6}
      />      
      <div className={verificationError ? "login__error login__error--margin-bottom" : "login__error login__error--hide"}>
          <span className="login__error--wrap">Your code has either expired or been entered incorrectly. Please try again or click below to resend a new code.</span>
      </div>
      <div className={unexpectedError ? "login__error" : "login__error login__error--hide"}>
        <span className="login__error--wrap">An unexpected error occurred, please try again later.</span>
      </div>
      <div className="login__line">
        {!props.isResendActivationOtp &&
        <ResendOtp 
          linkClassName={"loginP3__reperformPasscodeProcess"}
          verificationToken={props.verificationToken} 
          isResendingCallback={v => setIsResending(v)}
          onModeChanged={setOtpMode}
          analyticsRegion={props.analyticsRegion || "unknown"}
          analyticsAction={props.analyticsAction || "unknown"} />
        }
        {props.isResendActivationOtp &&
        <ResendActivationOtp
            linkClassName="login__link"
            mobileNumber={props.mobileNumber}
            isResendingCallback={(/** @type {boolean} */ v) => setIsResending(v)} />
        }
      </div>
      {renderNextButton("Next", isLoading, !isValid(), nextClicked)}
    </form>
  );
};

export default withFeatureFlags(VerifyOtp);